import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Mail, Building, Key, Lock, Eye, EyeOff, Info, ArrowLeft, ArrowRight, Loader2, Utensils, ShieldCheck } from 'lucide-react';
import axios from 'axios';
import Toast from '../../common/components/Toast';
import { RESTAURANT_API_URL } from '../../api/config';
import BgImage from '../../Pictures/restaurant.jpg';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function RestaurantForgotPassword() {
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState(location.state?.email || '');
  const [restaurantId, setRestaurantId] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  const showToast = (message, type = 'success') => setToast({ show: true, message, type });
  const closeToast = () => setToast(t => ({ ...t, show: false }));

  const handleRequestOTP = async () => {
    if (!EMAIL_RE.test(email.trim()) || !restaurantId.trim()) {
      showToast('Please enter valid email and Restaurant ID', 'error');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${RESTAURANT_API_URL}/auth/forgot-password/request-otp`, {
        email: email.trim().toLowerCase(),
        restaurantId: restaurantId.trim(),
      });
      if (res.data.success) {
        showToast('6-digit OTP code sent to your email', 'success');
        setStep(2);
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to send OTP code. Check credentials.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (!otp.trim()) {
      showToast('Please enter the 6-digit OTP code', 'error');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${RESTAURANT_API_URL}/auth/forgot-password/verify-otp`, {
        email: email.trim().toLowerCase(),
        restaurantId: restaurantId.trim(),
        otp: otp.trim(),
      });
      if (res.data.success) {
        showToast('OTP verified successfully!', 'success');
        setStep(3);
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Invalid or expired OTP code', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      showToast('Password must be at least 6 characters', 'error');
      return;
    }
    if (newPassword !== confirmPassword) {
      showToast('Passwords do not match', 'error');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${RESTAURANT_API_URL}/auth/forgot-password/reset-password`, {
        email: email.trim().toLowerCase(),
        restaurantId: restaurantId.trim(),
        otp: otp.trim(),
        newPassword,
      });
      if (res.data.success) {
        showToast('Password reset successfully! Redirecting to Sign In...', 'success');
        setTimeout(() => navigate('/signin'), 2000);
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to reset password', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="h-screen overflow-hidden flex flex-col"
      style={{
        backgroundImage: `url('${BgImage}')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      <div className="absolute inset-0 bg-black/55 dark:bg-black/70 pointer-events-none z-0" />
      {toast.show && <Toast message={toast.message} type={toast.type} onClose={closeToast} />}

      {/* Header */}
      <header className="relative z-10 flex-shrink-0 flex items-center justify-between px-6 md:px-12 py-4 border-b border-white/10 bg-white/10 dark:bg-black/20 backdrop-blur-md">
        <div className="flex items-center gap-3">
          <Utensils className="w-6 h-6 text-blue-400" />
          <h2 className="text-2xl font-bold text-white">TOBO RESTAURANT</h2>
        </div>
        <button onClick={() => navigate('/signin')} className="text-white/80 text-sm font-medium hover:text-white transition-colors flex items-center gap-1">
          Back to Restaurant Sign In
        </button>
      </header>

      {/* Main Form Container */}
      <main className="relative z-10 flex-1 flex items-center justify-center px-4 overflow-hidden">
        <div className="w-full max-w-[440px] max-h-full overflow-y-auto bg-white/90 dark:bg-gray-900/90 backdrop-blur-xl rounded-3xl border border-white/30 dark:border-gray-700/50 p-8 md:p-10 shadow-2xl">

          {/* Stepper Progress */}
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={() => {
                if (step > 1) setStep(s => s - 1);
                else navigate('/signin');
              }}
              className="flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-blue-600 transition-colors"
            >
              <ArrowLeft size={16} /> Back
            </button>
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
              <Utensils size={12} /> Password Reset
            </div>
          </div>

          {/* STEP 1: Email & Restaurant ID */}
          {step === 1 && (
            <div className="space-y-6">
              <div className="text-center">
                <div className="w-14 h-14 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Utensils className="w-7 h-7 text-blue-600 dark:text-blue-400" />
                </div>
                <h1 className="text-3xl font-black text-gray-900 dark:text-white mb-1">Forgot Password?</h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Enter your Email &amp; Restaurant ID to reset password
                </p>
              </div>

              <div className="space-y-4">
                <div className="flex flex-col gap-1.5">
                  <label className="text-sm font-semibold text-gray-900 dark:text-gray-200">Restaurant ID</label>
                  <div className="relative">
                    <Building className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-blue-600 dark:text-blue-400" />
                    <input
                      type="text"
                      value={restaurantId}
                      onChange={e => setRestaurantId(e.target.value)}
                      placeholder="e.g. REST123"
                      autoFocus
                      className="w-full h-12 rounded-xl pl-12 pr-4 border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-600/20 focus:border-blue-600 transition-all text-sm"
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-sm font-semibold text-gray-900 dark:text-gray-200">Registered Email Address</label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-blue-600 dark:text-blue-400" />
                    <input
                      type="email"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && handleRequestOTP()}
                      placeholder="restaurant@example.com"
                      className="w-full h-12 rounded-xl pl-12 pr-4 border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-600/20 focus:border-blue-600 transition-all text-sm"
                    />
                  </div>
                </div>

                <button
                  onClick={handleRequestOTP}
                  disabled={loading || !EMAIL_RE.test(email.trim()) || !restaurantId.trim()}
                  className="w-full h-12 rounded-2xl bg-blue-600 text-white font-bold text-sm shadow-lg shadow-blue-600/20 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
                >
                  {loading ? <><Loader2 className="animate-spin" size={18} /> Requesting OTP...</> : <>Send 6-Digit OTP <ArrowRight size={16} /></>}
                </button>
              </div>
            </div>
          )}

          {/* STEP 2: Enter 6-Digit OTP */}
          {step === 2 && (
            <div className="space-y-6">
              <div className="text-center">
                <div className="w-14 h-14 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Key className="w-7 h-7 text-blue-600 dark:text-blue-400" />
                </div>
                <h1 className="text-3xl font-black text-gray-900 dark:text-white mb-1">Enter Verification OTP</h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Enter the 6-digit OTP code sent to <br />
                  <span className="font-semibold text-gray-900 dark:text-gray-200">{email}</span>
                </p>
              </div>

              <div className="space-y-4">
                <div className="flex flex-col gap-1.5">
                  <label className="text-sm font-semibold text-gray-900 dark:text-gray-200">6-Digit OTP Code</label>
                  <div className="relative">
                    <Key className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-blue-600 dark:text-blue-400" />
                    <input
                      type="text"
                      maxLength={6}
                      value={otp}
                      onChange={e => setOtp(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && handleVerifyOTP()}
                      placeholder="123456"
                      autoFocus
                      className="w-full h-12 rounded-xl pl-12 pr-4 tracking-widest text-lg font-bold border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-600/20 focus:border-blue-600 transition-all"
                    />
                  </div>
                </div>

                <button
                  onClick={handleVerifyOTP}
                  disabled={loading || !otp.trim()}
                  className="w-full h-12 rounded-2xl bg-blue-600 text-white font-bold text-sm shadow-lg shadow-blue-600/20 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
                >
                  {loading ? <><Loader2 className="animate-spin" size={18} /> Verifying Code...</> : <>Verify &amp; Continue <ArrowRight size={16} /></>}
                </button>
              </div>
            </div>
          )}

          {/* STEP 3: Set New Password */}
          {step === 3 && (
            <div className="space-y-6">
              <div className="text-center">
                <div className="w-14 h-14 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center mx-auto mb-3">
                  <ShieldCheck className="w-7 h-7 text-emerald-600 dark:text-emerald-400" />
                </div>
                <h1 className="text-3xl font-black text-gray-900 dark:text-white mb-1">Set New Password</h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Create a new secure password for your Restaurant account
                </p>
              </div>

              <div className="space-y-4">
                <div className="flex flex-col gap-1.5">
                  <label className="text-sm font-semibold text-gray-900 dark:text-gray-200">New Password</label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-blue-600 dark:text-blue-400" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={newPassword}
                      onChange={e => setNewPassword(e.target.value)}
                      placeholder="Minimum 6 characters"
                      className="w-full h-12 rounded-xl pl-12 pr-12 border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-600/20 focus:border-blue-600 transition-all text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(v => !v)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-blue-600 transition-colors"
                    >
                      {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-sm font-semibold text-gray-900 dark:text-gray-200">Confirm New Password</label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-blue-600 dark:text-blue-400" />
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={e => setConfirmPassword(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && handleResetPassword()}
                      placeholder="Re-enter new password"
                      className="w-full h-12 rounded-xl pl-12 pr-12 border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-600/20 focus:border-blue-600 transition-all text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(v => !v)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-blue-600 transition-colors"
                    >
                      {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                </div>

                <button
                  onClick={handleResetPassword}
                  disabled={loading || !newPassword || newPassword.length < 6 || newPassword !== confirmPassword}
                  className="w-full h-12 rounded-2xl bg-blue-600 text-white font-bold text-sm shadow-lg shadow-blue-600/20 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
                >
                  {loading ? <><Loader2 className="animate-spin" size={18} /> Resetting Password...</> : 'Reset Restaurant Password'}
                </button>
              </div>
            </div>
          )}

        </div>
      </main>
    </div>
  );
}
